import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticleBySlug, articleList, type ArticleMeta } from "@/mocks/articles";
import { getFAQs } from "./components/shared";
import { GuideContent } from "./components/GuideContent";
import { ReviewContent } from "./components/ReviewContent";
import { ShippingContent } from "./components/ShippingContent";
import { CouponsContent } from "./components/CouponsContent";
import { LegitContent } from "./components/LegitContent";
import { WarehouseContent } from "./components/WarehouseContent";
import { AlternativesContent } from "./components/AlternativesContent";
import { SneakerFindsContent, FashionFindsContent, StreetwearFindsContent } from "./components/FindsContent";

const contentMap: Record<string, React.ComponentType> = {
  "sugargoo-spreadsheet-guide": GuideContent,
  "sugargoo-review": ReviewContent,
  "sugargoo-shipping-cost": ShippingContent,
  "sugargoo-coupons": CouponsContent,
  "sugargoo-legit": LegitContent,
  "sugargoo-warehouse": WarehouseContent,
  "sugargoo-alternatives": AlternativesContent,
  "sugargoo-sneaker-finds": SneakerFindsContent,
  "sugargoo-fashion-finds": FashionFindsContent,
  "sugargoo-streetwear-finds": StreetwearFindsContent,
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const ContentComponent = contentMap[article.slug];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <ArticleRenderer article={article} ContentComponent={ContentComponent} />
      </main>
      <Footer />
    </>
  );
}

function ArticleRenderer({ article, ContentComponent }: { article: ArticleMeta; ContentComponent?: React.ComponentType }) {
  return (
    <article className="w-full">
      {/* Article Header */}
      <header className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50 border-b border-background-200/50">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400 mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-500">{article.category}</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">{article.title.length > 50 ? article.title.slice(0, 50) + "..." : article.title}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-2xl">
            {article.description}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> Updated: August 2026
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-book-open-line"></i> {article.category}
            </span>
          </div>
        </div>
      </header>

      {/* Article Body */}
      <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {ContentComponent && <ContentComponent />}

          {/* Internal Links */}
          <InternalLinks currentSlug={article.slug} />

          {/* Final CTA */}
          <FinalCTA />
        </div>
      </div>

      {/* BlogPosting Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.description,
            "image": article.imageUrl,
            "datePublished": "2026-08-01",
            "dateModified": "2026-08-09",
            "author": {
              "@type": "Organization",
              "name": "Sugargoo Sheet",
              "url": "https://sugargoosheet.fit"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Sugargoo Sheet",
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://sugargoosheet.fit/${article.slug}`
            },
            "url": `https://sugargoosheet.fit/${article.slug}`
          })
        }}
      />

      {/* FAQ Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": getFAQs(article.slug).map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a.replace(/<[^>]*>/g, "") }
            }))
          })
        }}
      />
    </article>
  );
}

function InternalLinks({ currentSlug }: { currentSlug: string }) {
  return (
    <section className="mt-12 pt-8 border-t border-background-200/70">
      <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Explore More Sugargoo Resources</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {articleList.filter(a => a.slug !== currentSlug).map((a) => (
          <Link
            key={a.slug}
            to={`/${a.slug}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
            onClick={() => window.scrollTo(0, 0)}
          >
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-article-line text-primary-600 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-700 truncate">{a.title}</p>
              <p className="text-xs text-foreground-400">{a.readTime}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="mt-10 text-center py-8 px-6 rounded-2xl bg-primary-50 border border-primary-200">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
        Ready to Explore Sugargoo Products?
      </h2>
      <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-lg mx-auto">
        Browse thousands of curated items across all categories on our main store. Use our Sugargoo Spreadsheet to discover exactly what you are looking for.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          href="https://kaspreadsheet.com"
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
        >
          Sugargoo Spreadsheet
          <i className="ri-arrow-right-up-line"></i>
        </a>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm md:text-base font-semibold hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-line"></i>
          Back to Homepage
        </Link>
      </div>
    </section>
  );
}